import type { NavigateFunction } from 'react-router-dom';
import { composeRoute } from 'constants/routes';
import { useIsNewBillingEnabled } from 'app/hooks/useIsNewBillingEnabled';
import { openBlank, openUrl, routePathToUrl } from 'utils/url';

const LEGACY_BILLING_URL = '/billing/index';
const LEGACY_BILLING_PLANS_URL = '/billing/selectPlan';

type BillingTarget = 'billing' | 'billingPlans';

/**
 * The billing destination for upsell / trial CTAs.
 *
 * New-billing-enabled (migrated/v2) accounts must always land on the v2 billing
 * SPA page; the legacy v1 user-count selector conflicts with the v2 data model
 * (BUG-13). Non-migrated accounts keep the legacy v1 URLs — the v2 page itself
 * redirects them back to v1 on a 404, so consistency is preserved.
 *
 * Deliberately not exported: the v2 value is a router path WITHOUT the `/app`
 * basename, and handing it to `openUrl` / `openBlank` lands outside the SPA and
 * bounces back to the dashboard (BUG-39). Callers go through
 * {@link useOpenBillingPage}, which knows how to open each form.
 */
const billingPaths = (
  isNewBillingEnabled: boolean,
): Record<BillingTarget, string> => {
  if (isNewBillingEnabled) {
    const v2BillingUrl = composeRoute('settings.routes.billing.path');
    return { billing: v2BillingUrl, billingPlans: v2BillingUrl };
  }

  return {
    billing: LEGACY_BILLING_URL,
    billingPlans: LEGACY_BILLING_PLANS_URL,
  };
};

interface OpenBillingPageOptions {
  newTab?: boolean;
  /**
   * The caller's `useNavigate()`, for components inside the router: the v2
   * route is then reached client-side. Without it the v2 route is opened as a
   * full page load through the router basename.
   */
  navigate?: NavigateFunction;
}

/**
 * Opens the engine-aware billing destination (see `billingPaths`).
 *
 * v2 (NewBilling) → the SPA billing route: client-side via `navigate` when the
 * caller passes one, otherwise a page load through the `/app` basename
 * (`routePathToUrl`). Legacy → the v1 URLs as they are.
 *
 * Upsell CTAs must go through this rather than the `OldApp` billing URLs: those
 * are the legacy Braintree purchase pages, which have no engine guard, so a
 * NewBilling account sent there can buy a second, legacy subscription.
 */
export function useOpenBillingPage() {
  const isNewBillingEnabled = useIsNewBillingEnabled();
  const paths = billingPaths(isNewBillingEnabled);

  return (
    target: BillingTarget = 'billing',
    { newTab = false, navigate }: OpenBillingPageOptions = {},
  ) => {
    const path = paths[target];

    if (!isNewBillingEnabled) {
      if (newTab) {
        openBlank(path);
      } else {
        openUrl(path);
      }
      return;
    }

    if (navigate && !newTab) {
      navigate(path);
      return;
    }

    const url = routePathToUrl({ pathname: path });
    if (newTab) {
      openBlank(url);
    } else {
      openUrl(url);
    }
  };
}
